export const IteratorBlockDocumentationMarkdown = `
## What does this block do?

The Iterator Block processes each item in a provided list by executing a designated action. It can pause between iterations and handles errors based on your chosen strategy.

## When to use it?

When you need to perform a specific action or apply a specific sequence of blocks to every record in a list.

Examples:

- Outreach to each patient in a provided list
- Do chart scrubbing for one patient and suspect multiple HCCs
- Process a referral fax file and categorize each extracted order

## How to configure this block?

1. Provide the part of the block payload which contains the items. For example putting \`{{patientNames.data.records}}\` in the **List to iterate** field will get values from the \`patientNames\` block and specifically the \`records\` portion of the payload.

Example Input:

~~~json
{
  "records": [
    {
      "id": 1,
      "name": "Alice Smith",
      "age": 35
    },
    {
      "id": 2,
      "name": "Bob Johnson",
      "age": 42
    }
  ]
}
~~~

2. Next, provide what action you want taken on each item in the list. Use **Action Block** and set it to:
- LLM Prompt - to get an AI response to a prompt about each item.
- Scripted Flow - to perform a code block on each item.
- Rest - to make an API call for each item.
- Subflow - to run blocks in a subflow for each item.

To use specific data from the list in the action block you can reference it like this: \`{{iterationItem.data.name}}\`. In this case, you are saying that you'd like to use name from the context of each \`iterationItem\`.

Let's look at some examples.

## Configuration Examples

**Example 1: Script Action**

Use code to convert names to UPPER case. Do it for each name.

- **Input List**:

  ~~~json
  {
    "records": [
      {
        "id": 1,
        "name": "Alice Smith",
        "age": 35
      },
      {
        "id": 2,
        "name": "Bob Johnson",
        "age": 42
      }
    ]
  }
  ~~~

- **Script Action**:

  ~~~javascript
  return \`Hello, \${iterationItem.data.name?.toUpperCase()}!\`;
  ~~~

- **Example Output**:

  ~~~json
  [
    "Hello, ALICE SMITH!",
    "Hello, BOB JOHNSON!"
  ]
  ~~~

**Example 2: LLM Prompt**

Use AI to combine two fields from the payload in more natural language.

- **Input List**: Similar to previous example

- **Prompt**:

  ~~~
  Summarize the following patient record in one sentence:
  Name: {{iterationItem.data.name}}
  Age: {{iterationItem.data.age}}
  ~~~

- **Example Output**:

  ~~~json
  [
    "Alice Smith is a 35-year-old patient.",
    "Bob Johnson is a 42-year-old patient."
  ]
  ~~~

## Advanced Settings

Specify a unique **Item key** to change the return value from an array to an object. In the return object, the keys will be the unique value from each item at the **Item key** field and the values will be the return value from the action block.

**Error Resolution** helps you specify what you want to happen in case one or more items in the list result in an error. This can happen if an item is blank, different from other items or something fails during the iterations. There are three options:

- Continue - to move on to the next item in the list
- Fail - to stop iterating and make the block fail
- Retry - to attempt the same action on the item

**Waiting in between iterations** could be helpful if your list creates a set of action that require some time to pass to be completed. Perhaps you want to rate limit of how fast API calls are made as to not overload a system.

Set **Time Interval** to a number of seconds/minutes/hours you'd like to pass between each iteration.

## How to test

- Prepare your data to ensure the input list is available.
- Activate the mock output mode to simulate processing without triggering the actual action.
- Run the block to check that each item is processed correctly and that the aggregated results meet your expectations.
- Experiment with error handling settings by simulating failures and verifying that the block behaves as configured.

## Best Practices

- **Ensure Unique Identification**: Ensure the **Item key** is set to use a field with unique values to avoid unintended overwriting.
- **Validate Input Data**: Confirm that structured and contains all required fields.
- **Leverage Dynamic Data**: Use dynamic notation (e.g., \`{{…}}\`) to pass in upstream values, making your iterations flexible.
- **Optimize Delay Settings**: Adjust the iteration pause to balance performance with system constraints.
- **Test and Iterate**: Use the mock mode extensively during development to fine-tune error handling and output formatting.
`;
